import React, { useEffect, useRef, useState } from 'react';
import { FaSave }      from '@react-icons/all-files/fa/FaSave';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaTimes }     from '@react-icons/all-files/fa/FaTimes';
import { FaSpinner }   from '@react-icons/all-files/fa/FaSpinner';

// ── Field descriptor ───────────────────────────────────────────────────────

export type FieldType = 'text' | 'textarea' | 'number' | 'email' | 'date' | 'select' | 'checkbox' | 'autocomplete' | 'autocomplete-on-demand' | 'file';

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface FormField<T extends Record<string, any> = Record<string, any>> {
  /** Key in the form values object */
  name: keyof T;
  label: string;
  type?: FieldType;
  placeholder?: string;
  required?: boolean;
  /** Span all columns */
  fullWidth?: boolean;
  /** Only shown in edit mode (isNew === false) */
  editOnly?: boolean;
  /** Never shown in the form (e.g. hidden id field handled externally) */
  hidden?: boolean;
  /** Rendered as read-only plain text */
  readOnly?: boolean;
  rows?: number;
  options?: SelectOption[];
  // ── autocomplete-on-demand specific ──────────────────────────────────────
  /** Base URL; query params are appended automatically */
  url?: string;
  /** Name of the query parameter sent to the server (default: 'query') */
  queryParam?: string;
  /** Key in each response object used as the option value (default: 'id') */
  valueKey?: string;
  /** Key in each response object used as the option label (default: 'nombre') */
  labelKey?: string;
  /** Minimum characters before the first request is fired (default: 2) */
  minChars?: number;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Additional fixed query params appended to every request */
  extraParams?: Record<string, string>;
  /** Resolves the display text for a selected value.
   *  `value` is the committed ID string; `raw` is the full server object (available
   *  after the user picks from the dropdown, undefined on initial edit-mode load). */
  labelResolver?: (value: string, raw?: any) => string | undefined;
  /** Custom renderer for each row in the dropdown.
   *  Receives the raw server item, the computed option, and whether it is currently selected. */
  renderOption?: (raw: any, opt: SelectOption, isSelected: boolean) => React.ReactNode;
  /** Called when the user selects or clears a file (type === 'file').
   *  Receives the raw File object, or null when the file is removed. */
  onFileSelect?: (file: File | null) => void;
}

// ── Autocomplete sub-component ─────────────────────────────────────────────

interface AutocompleteFieldProps {
  value: string;
  options: SelectOption[];
  placeholder?: string;
  hasError?: boolean;
  onChange: (value: string) => void;
}

function AutocompleteField({ value, options, placeholder, hasError, onChange }: AutocompleteFieldProps) {
  const selected      = options.find(o => String(o.value) === String(value));
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const [inputText, setInputText] = useState(selected?.label ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync displayed text when the external value changes (e.g. entity load)
  useEffect(() => {
    const opt = options.find(o => String(o.value) === String(value));
    setInputText(opt?.label ?? '');
    setQuery('');
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        // If the user abandoned typing without choosing, restore the selected label
        const opt = options.find(o => String(o.value) === String(value));
        setInputText(opt?.label ?? '');
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value, options]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setInputText(e.target.value);
    setOpen(true);
  };

  const handleSelect = (opt: SelectOption) => {
    onChange(String(opt.value));
    setInputText(opt.label);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    setQuery('');
    setOpen(false);
  };

  const inputBorder = hasError ? '#ef4444' : open ? '#6366f1' : '#e2e8f0';
  const inputBg     = hasError ? '#fff5f5' : '#fff';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={inputText}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => { setOpen(true); setQuery(''); }}
          style={{
            padding: '9px 36px 9px 12px',
            border: `1px solid ${inputBorder}`,
            borderRadius: 8,
            fontSize: 14,
            color: '#1e293b',
            outline: 'none',
            boxSizing: 'border-box',
            width: '100%',
            background: inputBg,
            transition: 'border-color 0.15s',
          }}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            title="Limpiar"
            style={{
              position: 'absolute', right: 8, top: '50%',
              transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#94a3b8', padding: 2,
              display: 'flex', alignItems: 'center',
            }}
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <ul style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 220, overflowY: 'auto',
          margin: 0, padding: 0, listStyle: 'none',
          zIndex: 999,
        }}>
          {filtered.map(opt => (
            <li
              key={opt.value}
              onMouseDown={() => handleSelect(opt)}
              style={{
                padding: '9px 14px',
                cursor: 'pointer',
                fontSize: 14,
                color: String(opt.value) === String(value) ? '#6366f1' : '#1e293b',
                background: String(opt.value) === String(value) ? '#f0f0ff' : 'transparent',
                fontWeight: String(opt.value) === String(value) ? 600 : 400,
                borderBottom: '1px solid #f8fafc',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLLIElement).style.background = '#f8fafc'; }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLLIElement).style.background =
                  String(opt.value) === String(value) ? '#f0f0ff' : 'transparent';
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {open && filtered.length === 0 && query.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#94a3b8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 999,
        }}>
          Sin resultados
        </div>
      )}
    </div>
  );
}

// ── Autocomplete-on-demand sub-component ───────────────────────────────────

/** Extended option that also carries the original server payload */
interface OnDemandOption extends SelectOption {
  raw: any;
}

interface AutocompleteOnDemandFieldProps {
  value:          string;
  url:            string;
  queryParam?:    string;
  valueKey?:      string;
  labelKey?:      string;
  minChars?:      number;
  debounceMs?:    number;
  placeholder?:   string;
  hasError?:      boolean;
  extraParams?:   Record<string, string>;
  labelResolver?: (value: string, raw?: any) => string | undefined;
  renderOption?:  (raw: any, opt: SelectOption, isSelected: boolean) => React.ReactNode;
  onChange:       (value: string) => void;
}

function AutocompleteOnDemandField({
  value,
  url,
  queryParam    = 'query',
  valueKey      = 'id',
  labelKey      = 'nombre',
  minChars      = 2,
  debounceMs    = 300,
  placeholder,
  hasError,
  extraParams,
  labelResolver,
  renderOption,
  onChange,
}: AutocompleteOnDemandFieldProps) {
  const [inputText, setInputText] = useState('');
  const [results,   setResults]   = useState<OnDemandOption[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [open,      setOpen]      = useState(false);

  const containerRef     = useRef<HTMLDivElement>(null);
  const debounceRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef         = useRef<AbortController | null>(null);
  const labelResolverRef = useRef(labelResolver);
  // Stores the raw server object of the last item chosen from the dropdown
  const selectedRawRef   = useRef<any>(undefined);

  // Keep refs fresh on every render
  useEffect(() => { labelResolverRef.current = labelResolver; });

  /** Resolves the display text for the committed value, using the raw item when available */
  const resolveLabel = (v: string) =>
    labelResolverRef.current?.(v, selectedRawRef.current) ?? selectedRawRef.current?.[labelKey] ?? v;

  // Sync display text when the selected value changes (e.g. entity loaded in edit mode)
  useEffect(() => {
    if (value) {
      const resolved = resolveLabel(value);
      setInputText(resolved);
    } else {
      setInputText('');
      selectedRawRef.current = undefined;
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click and restore the committed label
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setInputText(value ? resolveLabel(value) : '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async (q: string) => {
    if (q.length < minChars) { setResults([]); setOpen(false); return; }
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const params = new URLSearchParams({ [queryParam]: q });
    if (extraParams) Object.entries(extraParams).forEach(([k, v]) => params.append(k, v));
    setLoading(true);
    try {
      const res  = await fetch(`${url}?${params}`, { signal: abortRef.current.signal });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const arr: any[] = Array.isArray(data) ? data : (data.content ?? data.data ?? []);
      setResults(arr.map(item => ({
        value: String(item[valueKey]),
        label: String(item[labelKey] ?? item[valueKey]),
        raw:   item,
      })));
      setOpen(true);
    } catch (err: any) {
      if (err.name !== 'AbortError') setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setInputText(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), debounceMs);
  };

  const handleSelect = (opt: OnDemandOption) => {
    selectedRawRef.current = opt.raw;
    onChange(String(opt.value));
    setInputText(resolveLabel(String(opt.value)));
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    selectedRawRef.current = undefined;
    onChange('');
    setInputText('');
    setResults([]);
    setOpen(false);
  };

  const inputBorder = hasError ? '#ef4444' : open ? '#6366f1' : '#e2e8f0';
  const inputBg     = hasError ? '#fff5f5' : '#fff';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={inputText}
          placeholder={placeholder ?? `Buscar… (mín. ${minChars} car.)`}
          onChange={handleInputChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          style={{
            padding: '9px 36px 9px 12px',
            border: `1px solid ${inputBorder}`, borderRadius: 8,
            fontSize: 14, color: '#1e293b', outline: 'none',
            boxSizing: 'border-box', width: '100%',
            background: inputBg, transition: 'border-color 0.15s',
          }}
          autoComplete="off"
        />
        {loading && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            color: '#94a3b8', display: 'flex', alignItems: 'center', pointerEvents: 'none',
          }}>
            <FaSpinner style={{ fontSize: 13, animation: 'spin 1s linear infinite' }} />
          </span>
        )}
        {!loading && value && (
          <button type="button" onClick={handleClear} title="Limpiar" style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: 2, display: 'flex', alignItems: 'center',
          }}>
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 220, overflowY: 'auto',
          margin: 0, padding: 0, listStyle: 'none', zIndex: 999,
        }}>
          {results.map(opt => (
            <li
              key={opt.value}
              onMouseDown={() => handleSelect(opt)}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: 14,
                color:      String(opt.value) === String(value) ? '#6366f1' : '#1e293b',
                background: String(opt.value) === String(value) ? '#f0f0ff' : 'transparent',
                fontWeight: String(opt.value) === String(value) ? 600 : 400,
                borderBottom: '1px solid #f8fafc', transition: 'background 0.1s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLLIElement).style.background = '#f8fafc'; }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLLIElement).style.background =
                  String(opt.value) === String(value) ? '#f0f0ff' : 'transparent';
              }}
            >
              {renderOption ? renderOption(opt.raw, opt, String(opt.value) === String(value)) : opt.label}
            </li>
          ))}
        </ul>
      )}

      {open && !loading && results.length === 0 && inputText.length >= minChars && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#94a3b8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 999,
        }}>
          Sin resultados
        </div>
      )}
    </div>
  );
}

// ── FileField sub-component ────────────────────────────────────────────────

interface FileFieldProps {
  /** Current filename (controlled — the string value from form state) */
  value: string;
  placeholder?: string;
  hasError?: boolean;
  onChange: (filename: string) => void;
  onFileSelect?: (file: File | null) => void;
  accept?: string;
}

function FileField({ value, placeholder, hasError, onChange, onFileSelect, accept }: FileFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      onChange(file.name);
      onFileSelect?.(file);
    }
    // reset so the same file can be re-selected after removal
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
    onFileSelect?.(null);
  };

  return (
    <div style={fs.root}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />

      {value ? (
        /* ── File selected: show name + remove button ── */
        <div style={{ ...fs.chip, ...(hasError ? fs.chipError : {}) }}>
          <span style={fs.filename} title={value}>{value}</span>
          <button
            type="button"
            style={fs.removeBtn}
            onClick={handleRemove}
            title="Quitar archivo"
          >
            <FaTimes size={11} />
          </button>
        </div>
      ) : (
        /* ── No file: show picker button ── */
        <button
          type="button"
          style={{ ...fs.pickBtn, ...(hasError ? fs.pickBtnError : {}) }}
          onClick={handlePick}
        >
          <span style={fs.pickIcon}>📎</span>
          {placeholder ?? 'Seleccionar archivo…'}
        </button>
      )}
    </div>
  );
}

const fs: Record<string, React.CSSProperties> = {
  root:       { display: 'flex', alignItems: 'center' },
  pickBtn:    {
    display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    color: '#475569', background: '#f8fafc',
    border: '1.5px dashed #cbd5e1', transition: 'border-color 0.15s, background 0.15s',
  },
  pickBtnError: { borderColor: '#ef4444', background: '#fff5f5', color: '#b91c1c' },
  pickIcon:   { fontSize: 15 },
  chip:       {
    display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%',
    padding: '6px 10px 6px 12px', borderRadius: 8,
    background: '#eff6ff', border: '1px solid #bfdbfe',
  },
  chipError:  { background: '#fff5f5', border: '1px solid #fecaca' },
  filename:   {
    fontSize: 13, fontWeight: 500, color: '#1d4ed8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
  },
  removeBtn:  {
    all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', width: 20, height: 20, borderRadius: '50%',
    color: '#6b7280', background: '#dbeafe', flexShrink: 0,
    transition: 'background 0.15s, color 0.15s',
  },
};

// ── Component props ────────────────────────────────────────────────────────

export interface PageFormProps<T extends Record<string, any> = Record<string, any>> {
  fields: FormField<T>[];
  values: T;
  errors?: Partial<Record<keyof T, string>>;
  isNew?: boolean;
  saving?: boolean;
  onFieldChange: (name: keyof T, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  /** Accent color for the submit button (defaults to #6366f1) */
  accentColor?: string;
  submitLabel?: string;
  cancelLabel?: string;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function PageForm<T extends Record<string, any>>({
  fields,
  values,
  errors = {},
  isNew = true,
  saving = false,
  onFieldChange,
  onSubmit,
  onCancel,
  accentColor = '#6366f1',
  submitLabel,
  cancelLabel = 'Cancelar',
}: PageFormProps<T>) {
  const visibleFields = fields.filter(f => {
    if (f.hidden) return false;
    if (f.editOnly && isNew) return false;
    return true;
  });

  return (
    <form onSubmit={onSubmit} style={s.card}>
      <div style={s.grid}>
        {visibleFields.map((field, idx) => {
          const error   = errors[field.name] as string | undefined;
          const value   = (values[field.name] ?? '') as string;
          const inputSt = { ...s.input, ...(error ? s.inputError : {}), ...(field.readOnly ? s.inputReadOnly : {}) };
          const colSpan: React.CSSProperties = {};

          const isCheckbox = field.type === 'checkbox';
          const checked    = value === 'true' || value === true as any;

          return (
            <div key={String(field.name)} style={{ ...s.fieldGroup, ...colSpan }}>

              {isCheckbox ? (
                /* Inline toggle row for booleans */
                <label style={s.checkRow}>
                  <input
                    type="checkbox"
                    style={s.checkInput}
                    checked={checked}
                    onChange={e => onFieldChange(field.name, String(e.target.checked))}
                  />
                  <span style={s.label}>
                    {field.label}
                    {field.required && <span style={s.required}> *</span>}
                  </span>
                </label>
              ) : (
                <label style={s.label}>
                  {field.label}
                  {field.required && <span style={s.required}> *</span>}
                </label>
              )}

              {!isCheckbox && (
                <>
                  {field.readOnly ? (
                    <div style={s.readOnlyValue}>{value || <span style={{ color: '#94a3b8' }}>—</span>}</div>

                  ) : field.type === 'textarea' ? (
                    <textarea
                      style={{ ...inputSt, height: (field.rows ?? 4) * 24, resize: 'vertical' }}
                      value={value}
                      placeholder={field.placeholder}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    />

                  ) : field.type === 'select' ? (
                    <select
                      style={inputSt}
                      value={value}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    >
                      <option value="">— Selecciona —</option>
                      {(field.options ?? []).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>

                  ) : field.type === 'autocomplete' ? (
                    <AutocompleteField
                      value={value}
                      options={field.options ?? []}
                      placeholder={field.placeholder ?? 'Buscar…'}
                      hasError={!!error}
                      onChange={v => onFieldChange(field.name, v)}
                    />

                  ) : field.type === 'autocomplete-on-demand' ? (
                    <AutocompleteOnDemandField
                      value={value}
                      url={field.url ?? ''}
                      queryParam={field.queryParam}
                      valueKey={field.valueKey}
                      labelKey={field.labelKey}
                      minChars={field.minChars}
                      debounceMs={field.debounceMs}
                      placeholder={field.placeholder}
                      hasError={!!error}
                      extraParams={field.extraParams}
                      labelResolver={field.labelResolver}
                      renderOption={field.renderOption}
                      onChange={v => onFieldChange(field.name, v)}
                    />

                  ) : field.type === 'file' ? (
                    <FileField
                      value={value}
                      placeholder={field.placeholder}
                      hasError={!!error}
                      onChange={v => onFieldChange(field.name, v)}
                      onFileSelect={field.onFileSelect}
                    />

                  ) : (
                    <input
                      style={inputSt}
                      type={field.type ?? 'text'}
                      value={value}
                      placeholder={field.placeholder}
                      autoFocus={idx === 0}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    />
                  )}
                </>
              )}

              {error && <span style={s.errorMsg}>{error}</span>}
            </div>
          );
        })}
      </div>

      <div style={s.actions}>
        <button type="button" style={s.btnSecondary} onClick={onCancel} disabled={saving}>
          <FaArrowLeft style={{ marginRight: 6 }} />
          {cancelLabel}
        </button>
        <button type="submit" style={{ ...s.btnPrimary, background: accentColor }} disabled={saving}>
          <FaSave style={{ marginRight: 6 }} />
          {saving ? 'Guardando…' : (submitLabel ?? 'Guardar')}
        </button>
      </div>
    </form>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  card: {
    background: '#fff', borderRadius: 14, padding: '28px 32px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    display: 'flex', flexDirection: 'column', gap: 24,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px 0',
  },
  fieldGroup:    { display: 'flex', flexDirection: 'column', gap: 4 },
  label:         { fontSize: 12, fontWeight: 600, color: '#475569', userSelect: 'none' } as React.CSSProperties,
  checkRow:      { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' } as React.CSSProperties,
  checkInput:    { width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer', flexShrink: 0 } as React.CSSProperties,
  required:      { color: '#ef4444' },
  input: {
    padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
    fontSize: 14, color: '#1e293b', outline: 'none',
    boxSizing: 'border-box', width: '100%', transition: 'border-color 0.15s',
    background: '#fff',
  },
  inputError:    { borderColor: '#ef4444', background: '#fff5f5' },
  inputReadOnly: { background: 'transparent', border: '1px solid transparent', padding: '9px 0', cursor: 'default' },
  readOnlyValue: { fontSize: 14, color: '#1e293b', padding: '9px 0', borderBottom: '1px solid #f1f5f9' },
  errorMsg:      { fontSize: 11, color: '#ef4444', marginTop: 2 },
  actions:       { display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid #f1f5f9' },
  btnPrimary:    { display: 'flex', alignItems: 'center', padding: '9px 20px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary:  { display: 'flex', alignItems: 'center', padding: '9px 20px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
